/**
 * My Lettaga 회원 초대 — 펌측 API (Lettaga backend 프록시 경유)
 *
 * 계약: docs/MEMBER_INVITE_IDENTITY.md, docs/CHAT_CONTRACT.md
 * - 초대 키 = contactCd(사람), email = 발송·로그인 속성
 * - 실제 실행은 MyLettaga API가 소유. 여기서는 Lettaga 프록시(/api/mylettaga/*)를 호출.
 */

import { apiClient } from '@shared/api/client';

export interface InvitePayload {
    /** 사람 키 (필수) */
    contactCd: string;
    /** 초대 발송 대상 이메일 (필수) */
    email: string;
    /** 회사 grant 대상 (선택) */
    entityCd?: string;
}

export type MlInviteStatus = 'none' | 'invited' | 'accepted' | 'connected' | 'expired' | 'active' | 'revoked';

export interface InviteStatusResult {
    email: string;
    /** 사람 키(정석 매칭용). MyLettaga가 결과에 포함하면 이메일 대신 이걸로 색인. */
    contactCd?: string | null;
    status: MlInviteStatus;
    invitedAt?: string | null;
    acceptedAt?: string | null;
    inviteCode?: string | null;
    // 회사명(companies)은 invites/status에서 제거됨 — GET /mylettaga/members 에서 받는다.
}

/** 프록시 공통 응답 래퍼 */
interface ProxyResponse<T> {
    ok: boolean;
    data?: T;
    error?: string;
}

/** 펌 회원(가입 계정) — GET /api/members */
export interface FirmMember {
    contactCd: string;
    email: string;               // = loginEmail (기존 유지)
    loginEmail?: string | null;  // 실제 로그인 이메일 (M_USER)
    invitedEmail?: string | null; // 초대 발송 이메일 (M_INVITE)
    name?: string | null;
    status: string;              // 'connected' 등
    joinedAt?: string | null;
    lastLoginAt?: string | null;
    companies?: string[];
}

export const myLettagaMembersApi = {
    /** 담당펌 소속 가입 회원 목록 (firmCd 서버 주입) */
    list: () => apiClient.get<ProxyResponse<{ members: FirmMember[] }>>('/mylettaga/members'),
};

/** 초대대상(eligible) — 펌 등기 기반 개인 + 소속회사 (Members Eligible 풀 소스) */
export interface EligibleCompany {
    entityCd: string;
    name: string;
    role: string | null;
    positionName?: string | null; // officer일 때 직위(Director/Secretary 등)
}
export interface EligiblePerson {
    contactCd: string;
    name: string | null;
    email: string;
    companies: EligibleCompany[];
}
export const myLettagaEligibleApi = {
    /** 담당펌(firmCd 서버 주입)에 등기로 엮인 초대가능 개인 목록 */
    list: () => apiClient.get<ProxyResponse<{ people: EligiblePerson[]; total: number }>>('/mylettaga/eligible'),
};

/** 펌 기능 entitlement (§10.9) — My Lettaga > Settings */
export interface FirmEntitlement {
    firmCd?: string;
    usesMyLettaga: boolean;
    features: string[];
    baselineVisible: boolean;
    updatedAt?: string | null;
}

export const myLettagaEntitlementApi = {
    /** 조회 (미설정이면 기본값) */
    get: () => apiClient.get<ProxyResponse<{ entitlement: FirmEntitlement; featureKeys: string[] }>>('/mylettaga/entitlements'),
    /** 저장 */
    save: (body: { usesMyLettaga: boolean; features: string[]; baselineVisible: boolean }) =>
        apiClient.put<ProxyResponse<{ entitlement: FirmEntitlement }>>('/mylettaga/entitlements', body),
};

/**
 * 알림 라우팅 설정 — 저장·정규화는 MyLettaga(M_NOTIFY_CONFIG) 소유. Lettaga는 프록시.
 *  구조: { version, default:[Recipient], events:{ "<event>": {useDefault, assignees:[Recipient], delivery} } }
 */
export interface NotifyRecipient { email: string; name?: string; memberContactCd?: string | null; lang?: string; }
export interface NotifyEventCfg { useDefault: boolean; assignees: NotifyRecipient[]; delivery: 'immediate' | 'digest'; }
export interface NotifyConfig { version?: number; default: NotifyRecipient[]; events: Record<string, NotifyEventCfg>; }
export const myLettagaNotifyApi = {
    /** 조회 (미설정이면 빈 설정) */
    get: () => apiClient.get<ProxyResponse<{ config: NotifyConfig }>>('/mylettaga/notify-config'),
    /** 저장 (정규화된 설정 반환) */
    save: (config: NotifyConfig) =>
        apiClient.put<ProxyResponse<{ config: NotifyConfig }>>('/mylettaga/notify-config', { config }),
};

/**
 * My Lettaga 구독/애드온 요청 상태 — Lettaga L_MKPLACE_SUBSCRIPTION 소유(승인 게이트).
 * 경로는 마켓 라우터(/api/mkplace/mylettaga/*), 프록시(/api/mylettaga)와 다름에 주의.
 * 응답은 { success, data } (Proxy{ ok, data } 아님).
 */
export interface MlSubscriptionStatus {
    /** 본체 구독: none 미신청 · requested 승인대기 · active 구독중 · cancelled 해제 */
    main: 'none' | 'requested' | 'active' | 'cancelled';
    /** 애드온: none 미신청 · pending 승인대기 · active 활성 */
    addons: { email: 'none' | 'pending' | 'active'; domain: 'none' | 'pending' | 'active' };
}
interface MlSubResponse {
    success: boolean;
    data: MlSubscriptionStatus;
    created?: string[];
    error?: { code: string; message: string };
}
export const myLettagaSubscriptionApi = {
    /** 현재 구독/애드온 상태 조회 */
    get: () => apiClient.get<MlSubResponse>('/mkplace/mylettaga/subscription'),
    /** 구독 신청(본체 + 선택 애드온 한 번에). 승인 대기(requested)로 생성. */
    subscribe: (addons: { email: boolean; domain: boolean } & Record<string, unknown>) =>
        apiClient.post<MlSubResponse>('/mkplace/mylettaga/subscribe', addons),
    /** 애드온 개별 신청(구독중일 때) */
    requestAddon: (kind: 'email' | 'domain', body?: Record<string, unknown>) =>
        apiClient.post<MlSubResponse>(`/mkplace/mylettaga/addons/${kind}/request`, body || {}),
    getEmailConfig: () =>
        apiClient.get<{ success: boolean; data: {
            configActive?: boolean; configExists?: boolean; configSender?: string | null;
            replyToEmail?: string | null; dnsProvider?: string | null; existingMail?: string | null;
            contactName?: string | null; contactEmail?: string | null; contactPhone?: string | null;
            display?: string;
        } | null }>('/mkplace/mylettaga/email-config'),
    /** 진행 중 애드온 요청 취소 */
    cancelAddon: (kind: 'email' | 'domain') =>
        apiClient.post<MlSubResponse>(`/mkplace/mylettaga/addons/${kind}/cancel`, {}),
    /** 구독(본체+진행 중 애드온) 취소 */
    cancel: () => apiClient.post<MlSubResponse>('/mkplace/mylettaga/cancel', {}),
};

/** 담당펌 브랜드 자산(아이콘·로고) — 저장·서빙은 MyLettaga(M_CLIENT_BRAND) 소유. Lettaga는 등록 진입점만. */
export interface FirmBrandState {
    clientCd?: string;
    hasIcon?: boolean;
    hasLogo?: boolean;
    hasLogoWhite?: boolean;
    brandColor?: string | null;
    version?: number;
    iconUrl?: string | null;
    logoUrl?: string | null;
    logoWhiteUrl?: string | null;
}
export const myLettagaBrandApi = {
    /** 현재 등록 상태 (clientCd 서버 주입) */
    get: () => apiClient.get<ProxyResponse<FirmBrandState>>('/mylettaga/firm-brand'),
    /** 부분 업서트 (multipart: icon/logo 파일, 보낸 자산만 갱신) */
    save: (fd: FormData) => apiClient.post<ProxyResponse<FirmBrandState>>('/mylettaga/firm-brand', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    /** 등록 해제. kind 지정 시 해당 자산만 삭제(나머지 유지), 없으면 전체 해제(폴백 복귀). */
    remove: (kind?: 'icon' | 'logo' | 'logo-white') =>
        apiClient.delete<ProxyResponse<FirmBrandState>>(`/mylettaga/firm-brand${kind ? `?kind=${kind}` : ''}`),
};

export const myLettagaInviteApi = {
    /** 단건 초대 발송 */
    invite: (payload: InvitePayload) => {
        return apiClient.post<ProxyResponse<any>>('/mylettaga/invites', payload);
    },

    /** 초대/연결 상태 일괄 조회 (순수 상태만; 회사명은 members).
     *  매칭은 contactCds(정석) 우선. 이메일은 폴백. 둘 중 하나 이상 전달. */
    status: (emails: string[], contactCds?: string[]) => {
        const body: { emails?: string[]; contactCds?: string[] } = {};
        if (emails?.length) body.emails = emails;
        if (contactCds?.length) body.contactCds = contactCds;
        return apiClient.post<ProxyResponse<{ results: InviteStatusResult[] }>>(
            '/mylettaga/invites/status',
            body,
        );
    },
};
